import { Duration, LocalDate } from '@js-joda/core';
import type {} from '@redux-devtools/extension'; // required for devtools typing
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { PaidBreakExceptionReason } from '#/shared/types/PaidBreakRequest';
import { ShiftEntry } from '#/shared/types/Timesheet';

type PaidBreakException = {
  break: Duration;
  reason: PaidBreakExceptionReason;
  notes?: string;
  approverDecision: 'accepted' | 'rejected' | 'pending' | 'error';
  approverNotes?: string;
};

interface PaidBreakExceptionStore {
  exceptions: Record<string, PaidBreakException | null>;
  setException: (shiftDate: LocalDate, exception: PaidBreakException | null) => void;
  setExceptionDecision: (shiftDate: LocalDate, decision: PaidBreakException['approverDecision']) => void;
  setExceptionNotes: (shiftDate: LocalDate, approverNotes: PaidBreakException['approverNotes']) => void;
  getException: (shiftDate: LocalDate) => PaidBreakException | null | undefined;
  initializeFromTimesheet: (shifts: ShiftEntry[]) => void;
  resetStore: () => void;
  getTotalExceptionsBreak: () => Duration;
  getAcceptedExceptionsBreak: () => Duration;
  rejectAllExceptions: () => void;
  acceptAllExceptions: () => void;
  hasAllAccepted: () => boolean;
  hasAllRejected: () => boolean;
  hasOutstandingPaidBreakExceptions: () => boolean;
  getFirstOutstandingPaidBreakExceptionDate: () => string | null;
  resetExceptionErrors: () => void;
}

/**
 * The store is used to store the paid break exceptions for each shift date.
 * The key is the shift date in the format of "YYYY-MM-DD".
 *
 * The store should be initialized with the paid break exceptions from the timesheets loaded from the API.
 */
export const usePaidBreakExceptionStore = create<PaidBreakExceptionStore>()(
  devtools(
    (set, get): PaidBreakExceptionStore => ({
      exceptions: {},
      setException: (shiftDate, exception) =>
        set(state => ({
          exceptions: {
            ...state.exceptions,
            [shiftDate.toString()]: exception,
          },
        })),
      getException: shiftDate => get().exceptions[shiftDate.toString()],
      setExceptionDecision: (shiftDate, decision) =>
        set(state => {
          const newExceptions = { ...state.exceptions };
          const shiftDateString = shiftDate.toString();

          if (newExceptions[shiftDateString]) {
            newExceptions[shiftDateString] = {
              ...newExceptions[shiftDateString],
              approverDecision: decision,
              approverNotes: decision === 'accepted' ? undefined : newExceptions[shiftDateString]?.approverNotes,
            };
          }
          return { exceptions: newExceptions };
        }),
      setExceptionNotes: (shiftDate, approverNotes) =>
        set(state => {
          const newExceptions = { ...state.exceptions };
          const shiftDateString = shiftDate.toString();

          if (newExceptions[shiftDateString]) {
            newExceptions[shiftDateString] = {
              ...newExceptions[shiftDateString],
              approverNotes,
            };
          }
          return { exceptions: newExceptions };
        }),
      rejectAllExceptions: () =>
        set(state => {
          const newExceptions = { ...state.exceptions };
          Object.entries(newExceptions).forEach(([shiftDate, exception]) => {
            if (exception) {
              newExceptions[shiftDate] = {
                ...exception,
                approverDecision: 'rejected',
              };
            }
          });
          return { exceptions: newExceptions };
        }),
      acceptAllExceptions: () =>
        set(state => {
          const newExceptions = { ...state.exceptions };
          Object.entries(newExceptions).forEach(([date, exception]) => {
            if (exception) {
              newExceptions[date] = {
                ...exception,
                approverDecision: 'accepted',
                approverNotes: undefined,
              };
            }
          });
          return { exceptions: newExceptions };
        }),
      initializeFromTimesheet: shifts =>
        set(state => {
          const newExceptions: Record<string, PaidBreakException | null> = {};

          shifts.forEach(shift => {
            const ordinaryEntry = shift.timeEntries.find(entry => entry.shiftType === 'Ordinary');

            if (ordinaryEntry?.paidBreakRequest) {
              const dateKey = shift.startAt.toString();
              newExceptions[dateKey] = {
                break: ordinaryEntry.paidBreakRequest.break,
                reason: ordinaryEntry.paidBreakRequest.reason,
                notes: ordinaryEntry.paidBreakRequest.notes,
                approverDecision: 'pending',
                approverNotes: undefined,
              };
            }
          });

          return {
            exceptions: {
              ...state.exceptions,
              ...newExceptions,
            },
          };
        }),
      resetStore: () =>
        set({
          exceptions: {},
        }),
      getTotalExceptionsBreak: () => {
        return Object.values(get().exceptions).reduce(
          (total, exception) => (exception ? total.plus(exception.break) : total),
          Duration.ZERO,
        );
      },
      getAcceptedExceptionsBreak: () => {
        return Object.values(get().exceptions).reduce(
          (acceptedTotal, exception) =>
            exception && exception.approverDecision === 'accepted'
              ? acceptedTotal.plus(exception.break)
              : acceptedTotal,
          Duration.ZERO,
        );
      },
      hasAllAccepted: () => {
        return Object.values(get().exceptions).every(
          exception => !exception || exception.approverDecision === 'accepted',
        );
      },
      hasAllRejected: () => {
        return Object.values(get().exceptions).every(
          exception => !exception || exception.approverDecision === 'rejected',
        );
      },
      hasOutstandingPaidBreakExceptions: () => {
        return Object.values(get().exceptions).some(
          exception => exception?.approverDecision === 'pending' || exception?.approverDecision === 'error',
        );
      },
      getFirstOutstandingPaidBreakExceptionDate: () => {
        const outstandingDates = Object.entries(get().exceptions)
          .filter(([, ex]) => ex?.approverDecision === 'pending' || ex?.approverDecision === 'error')
          .map(([date]) => date)
          .sort();
        return outstandingDates[0] ?? null;
      },
      resetExceptionErrors: () => {
        set(state => {
          const newExceptions = { ...state.exceptions };
          Object.entries(newExceptions).forEach(([shiftDate, exception]) => {
            if (exception && exception.approverDecision === 'error') {
              newExceptions[shiftDate] = {
                ...exception,
                approverDecision: 'pending',
              };
            }
          });
          return { exceptions: newExceptions };
        });
      },
    }),
    {
      name: 'paid-break-exception-storage',
    },
  ),
);
